import { useQuery } from "@tanstack/react-query";
import { createServerFn } from "@tanstack/react-start";
import { getCatalog } from "@/lib/core/catalog-core.server";
import {
    NearbyProvidersInputSchema,
    NearbyProvidersResultSchema,
    type NearbyProvidersInput,
} from "./provider.contract";

export type { NearbyProvidersInput };

const getNearbyProviders = createServerFn({ method: "GET" })
    .inputValidator((data: NearbyProvidersInput) =>
        NearbyProvidersInputSchema.parse(data),
    )
    .handler(async ({ data }) => {
        const catalog = await getCatalog();
        return NearbyProvidersResultSchema.parse(
            await catalog.organizations.getNearbyProviders(data),
        );
    });

export const useNearbyProvidersLiveQuery = (
    location: { lat: number; lng: number } | null,
    {
        page = 0,
        limit = 3,
        excludeOrganizationId,
    }: {
        page?: number;
        limit?: number;
        excludeOrganizationId?: string;
    } = {},
) => {
    return useQuery({
        queryKey: [
            "nearbyProviders",
            location?.lat,
            location?.lng,
            page,
            limit,
            excludeOrganizationId,
        ],
        queryFn: () => {
            if (!location) {
                throw new Error(
                    "Location is required to load nearby providers.",
                );
            }

            return getNearbyProviders({
                data: {
                    lat: location.lat,
                    lng: location.lng,
                    limit,
                    offset: page * limit,
                    excludeOrganizationId,
                },
            });
        },
        enabled: Boolean(location),
    });
};

export type NearbyProvider = Awaited<
    ReturnType<typeof getNearbyProviders>
>["providers"][number];
